const { get, noop } = require('lodash');
const {
    getUgcData: getGigsUgcData,
} = require('@fiverr-private/gig_listings/src/GigCardListings/externalUtils/getUgcData');
const { COMPONENT_TYPES } = require('../../utils/constants');

/**
 * This method creates a new component with each translated gig appended
 * @param { Object } component - The component to add the translated content to
 * @param { Object } translatedBlob - The translation response, should be keyed by gig ids
 */
const mergeTranslatedGigsContent = (component = {}, translatedBlob = {}) => ({
    ...component,
    items: {
        ...get(component, 'items', {}),
        gigs: get(component, 'items.gigs', []).map((item) => ({ ...item, ...translatedBlob[item.gig_id] })),
    },
});

/**
 * This function maps each known component type to its ugc-content handlers
 * for example, getGigsUgcData & mergeTranslatedGigsContent should be used for all the gigs components
 * @param { Object } component - The component to get handlers for
 * @returns Object that holds { zip, unzip } functions for the passed component according to its type (gigs/etc)
 */
const handleComponentUgcContent = (component) => {
    if (Object.values(COMPONENT_TYPES.GIGS).includes(component.type)) {
        return {
            zip: (targetLocale) => getGigsUgcData(get(component, 'items.gigs', []), targetLocale),
            unzip: (translatedBlob) => mergeTranslatedGigsContent(component, translatedBlob),
        };
    } else {
        return {
            zip: noop,
            unzip: () => component,
        };
    }
};

/**
 * This function returns all the UGC content from a personalizedContent object for translations proposes
 * @param { Object } personalizedContent - The personalizedContent to work on
 * @param { String } targetLocale - The targetLocale, used for predefined translations like the gigs 'I Will'
 * @returns Object that maps from each translatable entity ID to its relevant UGC data
 * @example
 * {
      '1339962': {
        translatedTitle: 'write the best quality articles for you on any topic'
      },
      '3424537': {
        translatedTitle: 'some other gig title'
      }
 * }
 */
const getUgcData = (personalizedContent = {}, targetLocale) => {
    const translatableContent = {};
    const { sections } = personalizedContent;

    if (!sections || !targetLocale) {
        return;
    }

    Object.values(sections).forEach((componentsArr) => {
        if (!componentsArr) {
            return;
        }

        componentsArr.forEach((component) => {
            const content = handleComponentUgcContent(component).zip(targetLocale);

            Object.assign(translatableContent, content);
        });
    });

    return translatableContent;
};

/**
 * Does the opposite of getUgcData, appends the translatedBlob back into the personalizedContent object
 * translatedBlob should be extracted out of the machine translation response
 * @param { Object } translatedBlob - The translation response
 * @param { Object } personalizedContent - The personalizedContent to append into
 */
const putUgcData = (translatedBlob = {}, personalizedContent = {}) => {
    const sectionsWithTranslatedGigs = {};

    const sections = get(personalizedContent, 'sections', {});

    Object.entries(sections).forEach(([sectionName, componentsArr]) => {
        const components = componentsArr || [];

        sectionsWithTranslatedGigs[sectionName] = components.map((component) =>
            handleComponentUgcContent(component).unzip(translatedBlob)
        );
    });

    return { sections: sectionsWithTranslatedGigs };
};

module.exports = {
    getUgcData,
    putUgcData,
};
